import { useEffect, useRef } from 'react';

/**
 * Auto-flips cards sequentially when the observed section scrolls into view.
 * Round 1: even-indexed cards (0, 2, 4, …)
 * Round 2: odd-indexed cards (1, 3, 5, …)
 * Each card stays flipped for 5 s, with 2 s between flip starts.
 */
export function useAutoFlipCards<T extends number | string>(
  sectionRef: React.RefObject<HTMLElement | null>,
  cardIds: T[],
  setFlipped: React.Dispatch<React.SetStateAction<Set<T>>>,
) {
  const hasRun = useRef(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Stabilise cardIds so the effect only re-runs when actual values change,
  // not when the parent creates a new array reference with the same contents
  // (e.g. CuratedExperiences filter effect re-setting filteredItineraries).
  const cardIdsKey = cardIds.join(',');

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || cardIds.length === 0 || hasRun.current) return;

    const evenIds = cardIds.filter((_, i) => i % 2 === 0);
    const oddIds  = cardIds.filter((_, i) => i % 2 !== 0);
    if (evenIds.length === 0 && oddIds.length === 0) return;

    const INITIAL_DELAY = 2000;
    const FLIP_DURATION = 5000;
    const INTERVAL      = 2000;

    const scheduleRound = (ids: T[], startOffset: number): number => {
      let offset = startOffset;
      ids.forEach((id) => {
        const flipTimer = setTimeout(() => {
          setFlipped(prev => { const n = new Set(prev); n.add(id); return n; });
        }, offset);
        timers.current.push(flipTimer);

        const unflipTimer = setTimeout(() => {
          setFlipped(prev => { const n = new Set(prev); n.delete(id); return n; });
        }, offset + FLIP_DURATION);
        timers.current.push(unflipTimer);

        offset += INTERVAL;
      });
      return offset - INTERVAL + FLIP_DURATION;
    };

    const cleanup = () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };

    let sequenceStarted = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasRun.current) return;
        hasRun.current = true;
        sequenceStarted = true;
        observer.disconnect();

        const roundOneEnd = scheduleRound(evenIds, INITIAL_DELAY);
        if (oddIds.length > 0) {
          scheduleRound(oddIds, roundOneEnd + INTERVAL);
        }
      },
      { threshold: 0, rootMargin: '0px 0px 100px 0px' },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      if (sequenceStarted) {
        // Effect re-ran after observer already fired (e.g. cardIds changed).
        // Reset so the next effect invocation can start fresh.
        cleanup();
        hasRun.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionRef, cardIdsKey, setFlipped]);
}
